import type { LedgerEvent, RegisterSettings, ServerEvent } from "@shared/events";
import type { LocalMeta, LocalStore, StoredEvent, SyncClient, SyncStatus } from "@client/db/contract";
import type { Role } from "@shared/api";
import { DEFAULT_SETTINGS } from "@shared/ledger";
import { uuidv7 } from "@shared/ids";

/**
 * OWNED BY C1. An in-memory LocalStore/SyncClient pair implementing the same contract as C4's Dexie
 * implementation. Two jobs:
 *   1. it is what every client test injects, so tests never touch IndexedDB;
 *   2. it is the fallback the shell uses when C4's createLocalStore is not available (see state/deps.ts),
 *      which keeps the app runnable during the build wave. It is NOT durable — F-2 "never lose a sale"
 *      is satisfied only by C4's store.
 */

export function createMemoryStore(init?: Partial<LocalMeta>): LocalStore {
  const meta: LocalMeta = {
    deviceId: init?.deviceId ?? uuidv7(),
    activeRegisterId: init?.activeRegisterId ?? null,
    lastServerOrder: init?.lastServerOrder ?? 0,
    configVersion: init?.configVersion ?? 0,
    nextSeq: init?.nextSeq ?? 0,
  };
  const rows: StoredEvent[] = [];
  const byId = new Map<string, StoredEvent>();
  const settings = new Map<string, RegisterSettings>();
  const roles = new Map<string, Role>();
  const drafts = new Map<string, unknown>();
  const listeners = new Set<() => void>();
  const notify = () => { for (const l of [...listeners]) l(); };

  const order = (list: StoredEvent[]) =>
    [...list].sort((a, b) => {
      const ao = a.serverOrder, bo = b.serverOrder;
      if (ao != null && bo != null) return ao - bo;
      if (ao != null) return -1;
      if (bo != null) return 1;
      return a.event.occurredAt - b.event.occurredAt || a.event.seq - b.event.seq;
    });

  return {
    async open() {},
    async getMeta() { return { ...meta }; },
    async setActiveRegister(registerId, next, role) {
      meta.activeRegisterId = registerId;
      settings.set(registerId, next);
      if (role) roles.set(registerId, role);
      notify();
    },
    async localRegisters() {
      const ids = new Set([...settings.keys(), ...rows.map((r) => r.event.registerId)]);
      return [...ids].map((id) => ({ id, role: roles.get(id) ?? null, settings: settings.get(id) ?? DEFAULT_SETTINGS }));
    },
    async appendEvents(events: LedgerEvent[]) {
      for (const event of events) {
        if (byId.has(event.id)) continue;
        const seq = Math.max(meta.nextSeq, event.seq);
        const stored: StoredEvent = { event: { ...event, seq }, syncState: "local", serverOrder: null };
        meta.nextSeq = seq + 1;
        rows.push(stored);
        byId.set(event.id, stored);
      }
      notify();
    },
    async loadEvents(registerId) {
      return order(rows.filter((r) => r.event.registerId === registerId));
    },
    async pendingEvents(registerId) {
      return order(rows.filter((r) => r.event.registerId === registerId && r.syncState === "local")).map((r) => r.event);
    },
    async markSent(ids) {
      for (const { id, serverOrder } of ids) {
        const row = byId.get(id);
        if (!row) continue;
        row.syncState = row.syncState === "saved" ? "saved" : "sent";
        row.serverOrder = serverOrder;
        meta.lastServerOrder = Math.max(meta.lastServerOrder, serverOrder);
      }
      notify();
    },
    async markSaved(ids) {
      for (const id of ids) {
        const row = byId.get(id);
        if (row) row.syncState = "saved";
      }
      notify();
    },
    async ingestServerEvents(events: ServerEvent[]) {
      for (const e of events) {
        const { serverOrder, receivedAt: _receivedAt, ...rest } = e;
        const existing = byId.get(e.id);
        if (existing) {
          existing.serverOrder = serverOrder;
          existing.syncState = existing.syncState === "local" ? "sent" : existing.syncState;
        } else {
          const stored: StoredEvent = { event: rest as LedgerEvent, syncState: "sent", serverOrder };
          rows.push(stored);
          byId.set(e.id, stored);
        }
        meta.lastServerOrder = Math.max(meta.lastServerOrder, serverOrder);
      }
      notify();
    },
    async saveCartDraft(registerId, draft) { drafts.set(registerId, draft); },
    async loadCartDraft(registerId) { return drafts.get(registerId) ?? null; },
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    async wipeRegister(registerId) {
      for (let i = rows.length - 1; i >= 0; i--) {
        const row = rows[i];
        if (row && row.event.registerId === registerId) {
          byId.delete(row.event.id);
          rows.splice(i, 1);
        }
      }
      drafts.delete(registerId);
      settings.delete(registerId);
      roles.delete(registerId);
      if (meta.activeRegisterId === registerId) meta.activeRegisterId = null;
      notify();
    },
  };
}

/** A SyncClient that never talks to a server. Reports the status it is told to report. */
export function createMemorySync(initial: SyncStatus = "offline"): SyncClient & { set(status: SyncStatus): void } {
  let status = initial;
  const listeners = new Set<(s: SyncStatus) => void>();
  return {
    start() {},
    stop() {},
    async flush() {},
    status: () => status,
    subscribe(listener) {
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },
    set(next: SyncStatus) {
      status = next;
      for (const l of [...listeners]) l(next);
    },
  };
}
